import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Spin, Typography, Progress, Tag, List, Badge } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { format, subDays, isToday, isSameDay } from 'date-fns';

const { Title, Text } = Typography;

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    overview: {
      totalParayanas: 0,
      activeParayanas: 0,
      completedParayanas: 0,
      totalCounts: 0,
      totalTargets: 0,
      overallProgress: 0,
      practiceStreak: 0,
      totalPracticeDays: 0
    },
    weeklyProgress: [],
    parayanaComparison: [],
    recentActivity: [],
    achievements: [],
    practicePatterns: {
      bestDay: '',
      averageDaily: 0,
      consistency: 0
    }
  });
  const { getUserId, getUserEmail } = useAuth();

  const fetchAnalytics = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      console.log('⏳ No user ID available for analytics');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📊 Fetching analytics for user:', userId);

      // 1. Fetch all parayanas
      const parayanaQuery = query(
        collection(db, `users/${userId}/parayanas`)
      );
      const parayanaSnapshot = await getDocs(parayanaQuery);
      const parayanas = parayanaSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📋 Found ${parayanas.length} parayanas for analytics`);

      // 2. Fetch all transactions for analytics
      const allTransactions = [];
      for (const parayana of parayanas) {
        try {
          const logsQuery = query(
            collection(db, `users/${userId}/parayanas/${parayana.id}/logs`)
          );
          const logsSnapshot = await getDocs(logsQuery);
          const transactions = logsSnapshot.docs.map(doc => ({
            id: doc.id,
            parayanaId: parayana.id,
            parayanaName: parayana.name,
            ...doc.data()
          }));
          allTransactions.push(...transactions);
        } catch (error) {
          console.log(`⚠️ Could not fetch logs for parayana ${parayana.id}:`, error);
          // Skip this parayana's logs if there's an error
        }
      }

      console.log(`📊 Processed ${allTransactions.length} total transactions`);

      // 3. Calculate overview statistics
      const totalParayanas = parayanas.length;
      const activeParayanas = parayanas.filter(p => (p.currentCount || 0) > 0 && (p.currentCount || 0) < p.targetCount).length;
      const completedParayanas = parayanas.filter(p => (p.currentCount || 0) >= p.targetCount).length;
      const totalCounts = parayanas.reduce((sum, p) => sum + (p.currentCount || 0), 0);
      const totalTargets = parayanas.reduce((sum, p) => sum + p.targetCount, 0);
      const overallProgress = totalTargets > 0 ? (totalCounts / totalTargets) * 100 : 0;

      // 4. Calculate practice streak and total practice days
      const { practiceStreak, totalPracticeDays } = calculateStreakAndDays(allTransactions);

      // 5. Generate weekly progress chart
      const weeklyProgress = generateWeeklyProgress(allTransactions);

      // 6. Generate parayana comparison
      const parayanaComparison = generateParayanaComparison(parayanas, allTransactions);

      // 7. Get recent activity
      const recentActivity = allTransactions
        .sort((a, b) => {
          const dateA = a.timestamp?.toDate?.() || new Date(0);
          const dateB = b.timestamp?.toDate?.() || new Date(0);
          return dateB - dateA;
        })
        .slice(0, 10);

      // 8. Generate achievements
      const achievements = generateAchievements(parayanas, allTransactions, {
        totalCounts,
        practiceStreak,
        completedParayanas,
        totalPracticeDays
      });

      // 9. Calculate practice patterns
      const practicePatterns = calculatePracticePatterns(allTransactions);

      setAnalytics({
        overview: {
          totalParayanas,
          activeParayanas,
          completedParayanas,
          totalCounts,
          totalTargets,
          overallProgress,
          practiceStreak,
          totalPracticeDays
        },
        weeklyProgress,
        parayanaComparison,
        recentActivity,
        achievements,
        practicePatterns
      });

      console.log('✅ Analytics calculation completed');

    } catch (error) {
      console.error('💥 Error fetching analytics:', error);
      // Analytics will show empty state if there's an error
    } finally {
      setLoading(false);
    }
  }, [getUserId]);

  const calculateStreakAndDays = (transactions) => {
    if (transactions.length === 0) return { practiceStreak: 0, totalPracticeDays: 0 };

    // Get unique practice days with better error handling
    const practiceDays = [...new Set(transactions
      .filter(t => t.timestamp) // Only transactions with valid timestamps
      .map(t => {
        try {
          const date = t.timestamp?.toDate?.() || new Date(t.timestamp);
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          console.log('⚠️ Invalid timestamp in transaction:', t.id);
          return null;
        }
      })
      .filter(Boolean) // Remove null values
    )].sort().reverse();

    const totalPracticeDays = practiceDays.length;

    // Calculate current streak with better validation
    let practiceStreak = 0;
    const today = format(new Date(), 'yyyy-MM-dd');
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');

    if (practiceDays.includes(today) || practiceDays.includes(yesterday)) {
      let currentDate = practiceDays.includes(today) ? today : yesterday;
      let dateIndex = practiceDays.indexOf(currentDate);
      
      while (dateIndex < practiceDays.length) {
        const expectedDate = format(subDays(new Date(currentDate), practiceStreak), 'yyyy-MM-dd');
        if (practiceDays[dateIndex] === expectedDate) {
          practiceStreak++;
          dateIndex++;
        } else {
          break;
        }
      }
    }

    return { practiceStreak, totalPracticeDays };
  };

  const generateWeeklyProgress = (transactions) => {
    const last14Days = [];
    for (let i = 13; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dayTransactions = transactions.filter(t => {
        if (!t.timestamp) return false;
        
        try {
          const tDate = t.timestamp?.toDate?.() || new Date(t.timestamp);
          return isSameDay(tDate, date);
        } catch (error) {
          return false; // Skip invalid timestamps
        }
      });
      
      const dayCount = dayTransactions.reduce((sum, t) => {
        const delta = t.countDelta || 0;
        return sum + Math.max(0, delta); // Only positive counts
      }, 0);
      
      last14Days.push({
        date: format(date, 'MMM dd'),
        count: dayCount,
        isToday: isToday(date)
      });
    }
    return last14Days;
  };

  const generateParayanaComparison = (parayanas, transactions) => {
    if (parayanas.length === 0) return [];
    
    return parayanas.map(parayana => {
      // Validate parayana data
      if (!parayana.targetCount || parayana.targetCount <= 0) {
        console.log('⚠️ Invalid target count for parayana:', parayana.name);
        return null;
      }
      
      const parayanaTransactions = transactions.filter(t => t.parayanaId === parayana.id);
      const totalCounts = parayanaTransactions.reduce((sum, t) => {
        const delta = t.countDelta || 0;
        return sum + Math.max(0, delta);
      }, 0);
      const progress = ((parayana.currentCount || 0) / parayana.targetCount) * 100;
      
      return {
        name: parayana.name && parayana.name.length > 15 
          ? parayana.name.substring(0, 15) + '...' 
          : parayana.name || 'Unnamed Parayana',
        current: parayana.currentCount || 0,
        target: parayana.targetCount,
        progress: Math.round(Math.min(progress, 100)), // Cap at 100%
        totalActivity: totalCounts
      };
    })
    .filter(Boolean) // Remove null entries
    .sort((a, b) => b.progress - a.progress);
  };

  const generateAchievements = (parayanas, transactions, stats) => {
    const achievements = [];

    if (stats.completedParayanas > 0) {
      achievements.push({ 
        title: stats.completedParayanas === 1 ? '🎉 First Completion' : '🎉 Multiple Completions', 
        description: `Completed ${stats.completedParayanas} parayana${stats.completedParayanas > 1 ? 's' : ''}`,
        color: '#52c41a'
      });
    }

    if (stats.practiceStreak >= 7) {
      const streakTitle = stats.practiceStreak >= 30 
        ? '🔥 Month Warrior' 
        : stats.practiceStreak >= 14 
        ? '🔥 Two Week Champion' 
        : '🔥 Week Warrior';
      achievements.push({ 
        title: streakTitle, 
        description: `${stats.practiceStreak} day practice streak!`,
        color: '#fa8c16'
      });
    }

    if (stats.totalCounts >= 10000) {
      achievements.push({ 
        title: '🏆 Master Devotee', 
        description: `${stats.totalCounts.toLocaleString()} total counts`,
        color: '#722ed1'
      });
    } else if (stats.totalCounts >= 1000) {
      achievements.push({ 
        title: '📿 Devotee', 
        description: `${stats.totalCounts.toLocaleString()} total counts`,
        color: '#722ed1'
      });
    }

    if (stats.totalPracticeDays >= 100) {
      achievements.push({ 
        title: '⭐ Dedicated Master', 
        description: `${stats.totalPracticeDays} days of practice`,
        color: '#1890ff'
      });
    } else if (stats.totalPracticeDays >= 30) {
      achievements.push({ 
        title: '⭐ Dedicated', 
        description: `${stats.totalPracticeDays} days of practice`,
        color: '#1890ff'
      });
    }

    return achievements;
  };

  const calculatePracticePatterns = (transactions) => {
    if (transactions.length === 0) {
      return {
        bestDay: 'No data yet',
        averageDaily: 0,
        consistency: 0
      };
    }
    
    const dayCount = {};
    let totalCounts = 0;

    transactions.forEach(t => {
      if (!t.timestamp) return;
      
      try {
        const date = t.timestamp?.toDate?.() || new Date(t.timestamp);
        const dayName = format(date, 'EEEE');
        const counts = Math.max(0, t.countDelta || 0);
        
        dayCount[dayName] = (dayCount[dayName] || 0) + counts;
        totalCounts += counts;
      } catch (error) {
        console.log('⚠️ Error processing transaction for patterns:', t.id);
      }
    });

    const bestDay = Object.keys(dayCount).length > 0
      ? Object.keys(dayCount).reduce((a, b) => dayCount[a] > dayCount[b] ? a : b, 'Sunday')
      : 'No data yet';
      
    const uniqueDays = [...new Set(transactions
      .filter(t => t.timestamp)
      .map(t => {
        try {
          const date = t.timestamp?.toDate?.() || new Date(t.timestamp);
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean)
    )];
    
    const averageDaily = uniqueDays.length > 0 ? Math.round(totalCounts / uniqueDays.length) : 0;
    const consistency = uniqueDays.length > 0 ? Math.min(100, Math.round((uniqueDays.length / 30) * 100)) : 0;

    return {
      bestDay,
      averageDaily,
      consistency
    };
  };

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
        <Spin size="large" />
        <Text style={{ color: '#FF8C00', fontSize: '16px', marginTop: '16px' }}>
          Calculating your spiritual journey...
        </Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <Card style={{ 
        marginBottom: 16,
        background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
        border: '1px solid #FFB347',
        borderRadius: '12px'
      }}>
        <Title level={3} style={{ color: '#FF8C00', margin: 0, textAlign: 'center' }}>
          📊 Your Spiritual Journey Analytics
        </Title>
        <Text style={{ color: '#FF7F00', display: 'block', textAlign: 'center', marginTop: '8px' }}>
          Insights into your devotional practice and progress
          {getUserEmail() && (
            <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
              • {getUserEmail()}
            </span>
          )}
        </Text>
      </Card>

      {/* Overview Statistics */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="🕉️ Total Parayanas"
              value={analytics.overview.totalParayanas}
              valueStyle={{ color: '#FF8C00', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="📿 Total Counts"
              value={analytics.overview.totalCounts.toLocaleString()}
              valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="🔥 Current Streak"
              value={analytics.overview.practiceStreak}
              suffix="days"
              valueStyle={{ color: '#faad14', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="📅 Practice Days"
              value={analytics.overview.totalPracticeDays}
              valueStyle={{ color: '#52c41a', fontSize: '20px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Progress Overview */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <Card title="🎯 Overall Progress" size="small">
            <Progress 
              type="circle" 
              percent={Math.round(analytics.overview.overallProgress)} 
              strokeColor={{
                '0%': '#FF8C00',
                '100%': '#52c41a',
              }}
              size={120}
              format={(percent) => `${percent}%`}
            />
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <Text style={{ fontSize: '14px', color: '#666' }}>
                {analytics.overview.totalCounts.toLocaleString()} of {analytics.overview.totalTargets.toLocaleString()} counts completed
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="📈 Practice Insights" size="small">
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#FF8C00' }}>Best Practice Day: </Text>
                <Tag color="orange">{analytics.practicePatterns.bestDay}</Tag>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#FF8C00' }}>Average Daily: </Text>
                <Text>{analytics.practicePatterns.averageDaily} counts</Text>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#FF8C00' }}>Active Parayanas: </Text>
                <Badge count={analytics.overview.activeParayanas} style={{ backgroundColor: '#faad14' }} />
              </div>
              <div>
                <Text strong style={{ color: '#FF8C00' }}>Completed: </Text>
                <Badge count={analytics.overview.completedParayanas} style={{ backgroundColor: '#52c41a' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="📅 Daily Progress (Last 14 Days)" size="small">
            <div style={{ height: 250 }}>
              {analytics.weeklyProgress.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.weeklyProgress}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#FFF4E6', 
                        border: '1px solid #FFB347',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#FF8C00"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                    <Text style={{ color: '#999' }}>No practice data yet</Text>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="🏆 Parayana Progress" size="small">
            <div style={{ height: 250, overflow: 'auto' }}>
              {analytics.parayanaComparison.length > 0 ? (
                analytics.parayanaComparison.map((item, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <Text strong style={{ fontSize: '12px' }}>{item.name}</Text>
                      <Text style={{ fontSize: '12px', color: '#666' }}>
                        {item.current}/{item.target}
                      </Text>
                    </div>
                    <Progress 
                      percent={item.progress} 
                      size="small"
                      strokeColor={item.progress === 100 ? '#52c41a' : item.progress >= 50 ? '#faad14' : '#FF8C00'}
                      showInfo={false}
                    />
                  </div>
                ))
              ) : (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🕉️</div>
                    <Text style={{ color: '#999' }}>No parayanas yet</Text>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Achievements and Recent Activity */}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12}>
          <Card title="🏅 Achievements" size="small">
            <div style={{ minHeight: '200px' }}>
              {analytics.achievements.length > 0 ? (
                <List
                  dataSource={analytics.achievements}
                  renderItem={item => (
                    <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <div>
                        <Text strong style={{ color: item.color, fontSize: '14px' }}>
                          {item.title}
                        </Text>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {item.description}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🌱</div>
                  <Text style={{ color: '#999' }}>Start practicing to unlock achievements!</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="⚡ Recent Activity" size="small">
            <div style={{ minHeight: '200px', maxHeight: '250px', overflow: 'auto' }}>
              {analytics.recentActivity.length > 0 ? (
                <List
                  dataSource={analytics.recentActivity.slice(0, 8)}
                  renderItem={item => {
                    const getTimestamp = (timestamp) => {
                      if (!timestamp) return new Date();
                      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                        return timestamp.toDate();
                      }
                      return timestamp instanceof Date ? timestamp : new Date(timestamp);
                    };

                    return (
                      <List.Item style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text style={{ fontSize: '12px', fontWeight: '500' }}>
                              {item.parayanaName} {item.countDelta > 0 ? '+' : ''}{item.countDelta}
                            </Text>
                            <Text style={{ fontSize: '10px', color: '#999' }}>
                              {format(getTimestamp(item.timestamp), 'MMM dd, HH:mm')}
                            </Text>
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
                  <Text style={{ color: '#999' }}>No recent activity</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsDashboard; 